<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Products Ordered</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0 ml-0 mr-0">View and manage order items</p>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Card -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Card Header -->
                                <div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                                        <div class="flex items-center gap-3 flex-wrap">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">All Orders</h2>
                                                <span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
                                                        {{ filteredItems.length }} total
                                                </span>
                                        </div>
                                        <div class="flex items-center gap-3 flex-wrap">
                                                <!-- Product Group Filter -->
                                                <div class="flex items-center gap-2">
                                                        <label class="text-sm text-gray-500 whitespace-nowrap">Group:</label>
                                                        <select
                                                                v-model="selectedGroup"
                                                                class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
                                                                @change="applyFilters"
                                                        >
                                                                <option value="">All Groups</option>
                                                                <option v-for="group in productGroups" :key="group.id" :value="group.id">
                                                                        {{ group.name }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <!-- Search -->
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search orders..."
                                                                class="py-2 pr-3 pl-9 border border-gray-300 rounded-lg text-sm w-56 outline-none transition-colors focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                                @input="applyFilters"
                                                        >
                                                </div>
                                                <!-- Refresh Button -->
                                                <button
                                                        type="button"
                                                        class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors"
                                                        @click="loadData"
                                                >
                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        Refresh
                                                </button>
                                        </div>
                                </div>

                                <!-- Table -->
                                <div class="overflow-x-auto">
                                        <!-- Loading State -->
                                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span class="ml-3 text-gray-500 text-sm">Loading orders...</span>
                                        </div>

                                        <!-- Empty State -->
                                        <div v-else-if="filteredItems.length === 0" class="text-center py-16 px-4">
                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                </svg>
                                                <h3 class="text-base font-medium text-gray-700 mb-1 mt-0">No orders found</h3>
                                                <p class="text-sm text-gray-500 m-0">Try adjusting your search or filters.</p>
                                        </div>

                                        <!-- Data Table -->
                                        <table v-else class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50 border-b border-gray-200">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Group</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">OrderItem ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">External ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Pages</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Print</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 transition-colors hover:bg-gray-50"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ getGroupName(item.groupid) }}</td>
                                                                <td class="py-3 px-4 text-sm">
                                                                        <button
                                                                                class="text-orange-7 hover:underline bg-transparent border-none cursor-pointer text-sm p-0"
                                                                                @click="goToOrder(item.orderID)"
                                                                        >
                                                                                {{ item.orderID }}
                                                                        </button>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm">
                                                                        <button
                                                                                class="text-orange-7 hover:underline bg-transparent border-none cursor-pointer text-sm p-0"
                                                                                @click="goToOrderItem(item.id)"
                                                                        >
                                                                                {{ item.id }}
                                                                        </button>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm">
                                                                        <button
                                                                                class="text-orange-7 hover:underline bg-transparent border-none cursor-pointer text-sm p-0"
                                                                                @click="goToUser(item.userid)"
                                                                        >
                                                                                {{ item.userid }}
                                                                        </button>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm">
                                                                        <a
                                                                                :href="getProductUrl(item)"
                                                                                target="_blank"
                                                                                class="text-orange-7 hover:underline no-underline"
                                                                        >
                                                                                {{ item.productid }}
                                                                        </a>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ item.externalOrderId || '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ item.quantity }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ item.pages }}</td>
                                                                <td class="py-3 px-4 text-sm font-medium text-gray-700">{{ formatValue(item) }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ item.offeringDescription || '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ formatDate(item.orderdate) }}</td>
                                                                <td class="py-3 px-4 text-center">
                                                                        <button
                                                                                class="text-orange-7 hover:text-orange-6 bg-transparent border-none cursor-pointer p-1"
                                                                                title="Download Print File"
                                                                                @click="showPrintModal(item)"
                                                                        >
                                                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                                </svg>
                                                                        </button>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} results
                                        </div>
                                        <div class="flex gap-1">
                                                <button
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage = 1"
                                                >First</button>
                                                <button
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >Prev</button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        class="py-2 px-3 text-sm border rounded-md cursor-pointer"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >{{ page }}</button>
                                                <button
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >Next</button>
                                                <button
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage = totalPages"
                                                >Last</button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Print URLs Modal -->
                <div v-if="showPrintUrlsModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-4">
                                <div class="flex items-center justify-between py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Print Files</h3>
                                        <button
                                                class="text-gray-400 hover:text-gray-500 bg-transparent border-none cursor-pointer p-1"
                                                @click="showPrintUrlsModal = false"
                                        >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div v-if="isLoadingPrintUrls" class="flex items-center justify-center p-4">
                                                <svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                        </div>
                                        <div v-else-if="printUrls.length === 0" class="text-center text-gray-500">
                                                No print files available
                                        </div>
                                        <div v-else class="flex flex-col gap-2">
                                                <a
                                                        v-for="(url, index) in printUrls"
                                                        :key="index"
                                                        :href="url"
                                                        target="_blank"
                                                        class="block p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-orange-7 no-underline break-all text-sm"
                                                >
                                                        {{ url }}
                                                </a>
                                        </div>
                                </div>
                                <div class="flex justify-end py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                class="py-2 px-4 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer"
                                                @click="showPrintUrlsModal = false"
                                        >
                                                Close
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, Ref } from 'vue';
import { useRouter, Router } from 'vue-router';
import OrderItemService from 'services/OrderItemService';
import OfferingService from 'services/OfferingService';
import ProductGroupService from 'services/ProductGroupService';
import { creatorDomain } from 'utils/http';
import type { OfferingModel } from '@sosocio/creator/interfaces';

// Extended OrderItem type for list view with additional computed fields
interface OrderItemRow {
        id: number;
        orderID: number;
        userid: number;
        productid: string;
        externalOrderId?: string;
        quantity: number;
        pages: number;
        salesvalue?: number;
        currency?: string;
        offeringDescription?: string;
        orderdate?: string;
        groupid?: number;
        read_token?: string;
}

interface ProductGroup {
        id: number;
        name: string;
}

export default defineComponent({
        name: 'ProductsOrderedPage',
        setup() {
                const router: Router = useRouter();

                const items: Ref<OrderItemRow[]> = ref([]);
                const offerings: Ref<OfferingModel[]> = ref([]);
                const productGroups: Ref<ProductGroup[]> = ref([]);
                const searchQuery: Ref<string> = ref('');
                const selectedGroup: Ref<string> = ref('');
                const currentPage: Ref<number> = ref(1);
                const pageSize: number = 50;
                const isLoading: Ref<boolean> = ref(true);
                const showPrintUrlsModal: Ref<boolean> = ref(false);
                const printUrls: Ref<string[]> = ref([]);
                const isLoadingPrintUrls: Ref<boolean> = ref(false);

                const filteredItems = computed<OrderItemRow[]>(() => {
                        let result = items.value;

                        if (selectedGroup.value) {
                                result = result.filter((item: OrderItemRow) => item.groupid === parseInt(selectedGroup.value, 10));
                        }

                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter((item: OrderItemRow) =>
                                        String(item.orderID).includes(query) ||
                                        String(item.id).includes(query) ||
                                        String(item.userid).includes(query) ||
                                        String(item.productid).includes(query) ||
                                        item.externalOrderId?.toLowerCase().includes(query) ||
                                        item.offeringDescription?.toLowerCase().includes(query)
                                );
                        }

                        return result;
                });

                const totalPages = computed<number>(() => Math.ceil(filteredItems.value.length / pageSize));

                const paginatedItems = computed<OrderItemRow[]>(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredItems.value.slice(start, start + pageSize);
                });

                const visiblePages = computed<number[]>(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const applyFilters = (): void => {
                        currentPage.value = 1;
                };

                const loadData = async (): Promise<void> => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const orderParams = new URLSearchParams({ limit: '0', orderby: 'orderdate DESC' });
                                const [orderData, offeringData, groupData] = await Promise.all([
                                        OrderItemService.getOrderSummary(orderParams),
                                        OfferingService.getAll(params),
                                        ProductGroupService.getAll(params),
                                ]);
                                items.value = orderData as unknown as OrderItemRow[];
                                offerings.value = offeringData;
                                productGroups.value = groupData;
                        } catch (error: unknown) {
                                if (error instanceof Error) {
                                        console.error('Failed to load data:', error.message);
                                } else {
                                        console.error('Failed to load data:', error);
                                }
                        } finally {
                                isLoading.value = false;
                        }
                };

                const getGroupName = (groupId: number | undefined): string => {
                        const group = productGroups.value.find((g: ProductGroup) => g.id === groupId);
                        return group?.name || '-';
                };

                const getProductUrl = (item: OrderItemRow): string => {
                        return `${creatorDomain}/app/showcase/${item.productid}/${item.read_token}`;
                };

                const formatValue = (item: OrderItemRow): string => {
                        if (item.salesvalue !== undefined && item.currency) {
                                return `${item.currency} ${item.salesvalue}`;
                        }
                        return item.salesvalue !== undefined ? String(item.salesvalue) : '-';
                };

                const formatDate = (dateStr: string | undefined): string => {
                        if (!dateStr) return '-';
                        const date = new Date(dateStr);
                        return date.toLocaleDateString();
                };

                const goToOrder = (orderId: number): void => {
                        router.push(`/showOrder/${orderId}`);
                };

                const goToOrderItem = (orderItemId: number): void => {
                        router.push(`/orderItem/${orderItemId}`);
                };

                const goToUser = (userId: number): void => {
                        router.push(`/appuser/${userId}`);
                };

                const showPrintModal = async (item: OrderItemRow): Promise<void> => {
                        showPrintUrlsModal.value = true;
                        isLoadingPrintUrls.value = true;
                        printUrls.value = [];
                        try {
                                const urls = await OrderItemService.getPrintUrls(item.id);
                                printUrls.value = urls;
                        } catch (error: unknown) {
                                if (error instanceof Error) {
                                        console.error('Failed to load print URLs:', error.message);
                                } else {
                                        console.error('Failed to load print URLs:', error);
                                }
                        } finally {
                                isLoadingPrintUrls.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        items,
                        offerings,
                        productGroups,
                        searchQuery,
                        selectedGroup,
                        currentPage,
                        pageSize,
                        isLoading,
                        filteredItems,
                        totalPages,
                        paginatedItems,
                        visiblePages,
                        showPrintUrlsModal,
                        printUrls,
                        isLoadingPrintUrls,
                        applyFilters,
                        loadData,
                        getGroupName,
                        getProductUrl,
                        formatValue,
                        formatDate,
                        goToOrder,
                        goToOrderItem,
                        goToUser,
                        showPrintModal,
                };
        },
});
</script>

<style scoped>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
